import React, { useRef, useState } from 'react';
import { Plus, X, Upload, Loader2, FileText, FileSpreadsheet, Presentation, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const iconForDocument = (doc) => {
  const name = doc.name?.toLowerCase() || '';
  if (doc.isFramework || name.includes('framework')) return <Sparkles className="h-5 w-5" />;
  if (name.endsWith('.ppt') || name.endsWith('.pptx')) return <Presentation className="h-5 w-5" />;
  if (name.endsWith('.xls') || name.endsWith('.xlsx')) return <FileSpreadsheet className="h-5 w-5" />;
  return <FileText className="h-5 w-5" />;
};

const typeLabel = (doc) => {
  if (doc.isFramework) return 'Framework';
  if (doc.type === 'brand') return 'Brand';
  if (doc.type === 'template') return 'Template';
  if (doc.type === 'guideline') return 'Guideline';
  return 'Document';
};

const typeBadgeClass = (doc) => {
  if (doc.isFramework) return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
  switch (doc.type) {
    case 'brand':
      return 'bg-purple-500/10 text-purple-500 border-purple-500/30';
    case 'template':
      return 'bg-green-500/10 text-green-500 border-green-500/30';
    case 'guideline':
      return 'bg-orange-500/10 text-orange-500 border-orange-500/30';
    default:
      return 'bg-muted text-muted-foreground border-border/60';
  }
};

const KnowledgeBase = ({
  companyDocs,
  onAddDocuments,
  onRemoveDocument,
  knowledgeStats
}) => {
  const companyDocsInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      await onAddDocuments(files);
    } catch (error) {
      console.error('Error uploading company documents:', error);
    } finally {
      setUploading(false);
      if (companyDocsInputRef.current) {
        companyDocsInputRef.current.value = '';
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const emptyState = (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/70 bg-background/50 py-12 text-center">
      <Upload className="h-12 w-12 text-muted-foreground" />
      <h4 className="mt-4 text-base font-semibold text-foreground">No documents uploaded yet</h4>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Add design manuals, brand frameworks, and templates so your AI guidance reflects your visual language.
      </p>
      <Button
        variant="outline"
        className="mt-6"
        onClick={() => companyDocsInputRef.current?.click()}
      >
        <Plus className="mr-2 h-4 w-4" /> Upload first document
      </Button>
    </div>
  );

  return (
    <div className="space-y-8">
      <header className="space-y-2 text-center">
        <h2 className="text-2xl font-semibold text-foreground">Company design knowledge</h2>
        <p className="text-sm text-muted-foreground">
          Keep your frameworks and brand references close so the AI can stay on-brand automatically.
        </p>
      </header>

      <section className="rounded-3xl border border-border bg-card/80 p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Design documents</p>
            <p className="text-xs text-muted-foreground">
              {companyDocs.length} document{companyDocs.length === 1 ? '' : 's'} uploaded
              {knowledgeStats?.hasFramework && ' � Design Compass framework active'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {uploading && (
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing files�
              </span>
            )}
            <Button onClick={() => companyDocsInputRef.current?.click()} disabled={uploading}>
              <Plus className="mr-2 h-4 w-4" /> Add documents
            </Button>
          </div>
        </div>

        <input
          ref={companyDocsInputRef}
          type="file"
          multiple
          accept=".txt,.pdf,.doc,.docx,.ppt,.pptx"
          onChange={handleFileUpload}
          className="hidden"
        />

        <div className="mt-6 space-y-3">
          {companyDocs.length === 0 ? (
            emptyState
          ) : (
            companyDocs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-4 rounded-2xl border border-border/60 bg-background/80 p-4 shadow-sm"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                  {iconForDocument(doc)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-medium text-foreground">{doc.name}</p>
                    <Badge variant="secondary" className={`border ${typeBadgeClass(doc)}`}>
                      {typeLabel(doc)}
                    </Badge>
                    {doc.size && (
                      <span className="text-xs text-muted-foreground">{formatFileSize(doc.size)}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Added {doc.uploadDate ? new Date(doc.uploadDate).toLocaleDateString() : 'recently'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => onRemoveDocument(doc.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>

        {knowledgeStats?.hasFramework && (
          <div className="mt-6 rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4 text-sm text-blue-700 dark:text-blue-200">
            <p className="font-medium text-blue-800 dark:text-blue-200">Design Compass active</p>
            <p className="mt-1 leading-relaxed">
              Your framework shapes every recommendation, slide analysis, and chat response. Upload updated frameworks here whenever your methodology changes.
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default KnowledgeBase;
